// PowerSync provider (Phase 1).
//
// Поведение:
//   - Локальная БД открывается не всем (`lib/powersync/local-db-gate.ts`): если
//     на устройстве уже есть владелец базы — сразу, до ответа о сессии (офлайн-
//     чтение своего кэша с протухшей сессией); иначе — только когда сессия
//     подтверждена. Гостю и краулеру на публичных страницах база не нужна, а
//     её открытие стоило worker + WASM + сторожа worker-режима, который на
//     медленной среде заводил в Hawk ложный «timeout» (OXEM-588). Пока базы нет,
//     в контексте лежит «спящая» база (`dormant-db.ts`): запросы пусты, синк не
//     подключён, а её смену на настоящую SDK-хуки отрабатывают сами.
//   - До авторизации WebSocket НЕ открывается, fetchCredentials не вызывается —
//     иначе PowerSync будет ругаться 401 в логи.
//   - Как только `useAuth().isAuthenticated === true` — `connect()` стартует
//     фоновую sync-сессию (через `StudyHubConnector` → `fetchPowerSyncCredentials`).
//   - При потере аутентификации — только `disconnect()`. Локальная SQLite и
//     upload-очередь СОХРАНЯЮТСЯ: сюда попадает и обычное истечение сессии, а
//     стирать в этот момент офлайн-правки — молчаливая потеря данных.
//     Полная очистка (`disconnectAndClearPowerSync`) живёт в `lib/logout.ts` и
//     срабатывает только на явный выход пользователя.
//   - Подключение привязано к КОНКРЕТНОМУ user.id, а не к флагу «залогинен».
//     Смена аккаунта без промежуточного разлогина (протухшая сессия → вход
//     другим, вторая вкладка, оборванный logout) обязана поднять синк заново на
//     чистой базе — этим занимается `connectPowerSyncForUser`.
import type { CommonPowerSyncDatabase } from "@powersync/common";
import { PowerSyncContext } from "@powersync/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import {
	connectPowerSyncForUser,
	disconnectPowerSync,
	getPowerSync,
	peekPowerSync,
	readPowerSyncOwner,
} from "@/lib/powersync/client";
import { DORMANT_POWERSYNC, isDormantPowerSync } from "@/lib/powersync/dormant-db";
import { suspendPowerSyncAuth } from "@/lib/powersync/jwt-provider";
import { shouldOpenLocalDb } from "@/lib/powersync/local-db-gate";
import { useAuth } from "./auth-provider";

/** БД на момент монтирования: уже открытая, своя база устройства или спящая. */
function initialLocalDb(): CommonPowerSyncDatabase {
	const opened = peekPowerSync();
	if (opened) return opened;
	return shouldOpenLocalDb({ authenticatedUserId: null, owner: readPowerSyncOwner() })
		? getPowerSync()
		: DORMANT_POWERSYNC;
}

export function PowerSyncProvider({ children }: { children: ReactNode }) {
	const [db, setDb] = useState(initialLocalDb);
	const { isAuthenticated, isLoading, user } = useAuth();
	const userId = user?.id ?? null;
	// Для кого уже поднят синк. null — не подключены. Сравнение по id, а не
	// булевым флагом: иначе вход другим аккаунтом в той же вкладке считался бы
	// «уже подключены» и оставался на чужой сессии.
	const connectedForRef = useRef<string | null>(null);
	// Гостя разъединяем один раз, а не на каждый рендер.
	const guestDisconnectedRef = useRef(false);

	useEffect(() => {
		if (isLoading) return;

		if (isAuthenticated && userId) {
			guestDisconnectedRef.current = false;
			// Сессия подтверждена — база нужна, даже если на устройстве её ещё не
			// было (первый вход). Открываем ДО connect: синк пишет в неё же.
			if (isDormantPowerSync(db)) setDb(getPowerSync());
			if (connectedForRef.current === userId) return;
			connectedForRef.current = userId;
			void connectPowerSyncForUser(userId).catch((err) => {
				// Лог в console: provider не имеет доступа к Pino. Реальная ошибка
				// (401, network) — PowerSync сам retry'ит. Если упало совсем — баннер
				// offline покажется отдельно через useSyncStatus (Phase 5).
				console.warn("[powersync-provider] connect failed", err);
			});
			return;
		}

		// Сессии нет — синка быть не должно. Разъединяем ДАЖЕ если эта вкладка
		// сама ничего не подключала: на вебе поток синка живёт в SharedWorker,
		// общем на весь origin, и он переживает и закрытие вкладки, и выход. У
		// гостя, открывшего публичную страницу, воркер продолжал просить
		// учётные данные и биться в `/api/sync/powersync/auth` — ровно это и
		// видно было в консоли на `/catalog`. Сессия хранится в cookie, общей
		// для вкладок, поэтому «гость здесь» означает «гость везде», и глушить
		// чужой синк этим нельзя.
		if (!isAuthenticated && (connectedForRef.current || !guestDisconnectedRef.current)) {
			connectedForRef.current = null;
			guestDisconnectedRef.current = true;
			// Не просто сброс кэша, а остановка выдачи токенов: воркер продолжает
			// просить учётные данные и после `disconnect()`, и каждая просьба без
			// сессии уходила бы в сеть за 401.
			suspendPowerSyncAuth();
			void disconnectPowerSync().catch((err) => {
				console.warn("[powersync-provider] disconnect failed", err);
			});
		}
	}, [db, isAuthenticated, isLoading, userId]);

	return <PowerSyncContext.Provider value={db}>{children}</PowerSyncContext.Provider>;
}
